import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { BoltIcon } from '@heroicons/react/24/outline';

interface TaskNodeData {
  task: {
    id: string;
    name: string;
    description: string | null;
    status: 'pending' | 'in_progress' | 'completed' | 'blocked';
    task_type: 'milestone' | 'task' | 'compliance_task';
    metadata?: any;
  };
  selected: boolean;
  onSelect: () => void;
  onStatusChange: (status: string) => void;
}

export const TaskNode = memo(({ data }: { data: TaskNodeData }) => {
  const { task, selected, onSelect, onStatusChange } = data;

  const getStatusColor = () => {
    switch (task.status) {
      case 'completed':
        return 'bg-green-100 border-green-500 text-green-900';
      case 'in_progress':
        return 'bg-blue-100 border-blue-500 text-blue-900 animate-pulse';
      case 'blocked':
        return 'bg-red-100 border-red-500 text-red-900';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-700';
    }
  };

  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5 text-blue-600" />;
      case 'blocked':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <LockClosedIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getProgressBar = () => {
    const progress = task.metadata?.progress || 0;
    if (task.status !== 'in_progress' || !progress) return null;

    return (
      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  };

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={`
          px-4 py-3 rounded-lg shadow-lg border-2 cursor-pointer transition-all duration-200
          ${getStatusColor()}
          ${selected ? 'ring-2 ring-offset-2 ring-blue-500' : ''}
          ${task.status === 'in_progress' ? 'shadow-xl' : ''}
          min-w-[200px] max-w-[250px]
        `}
        onClick={onSelect}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <h3 className="text-sm font-semibold line-clamp-2">{task.name}</h3>
              {task.status === 'in_progress' && (
                <BoltIcon className="h-4 w-4 text-yellow-500 animate-bounce" />
              )}
            </div>
            {task.description && (
              <p className="text-xs mt-1 opacity-75 line-clamp-2">{task.description}</p>
            )}
            {getProgressBar()}
          </div>
        </div>
        
        {/* Quick status change buttons for advisor view */}
        {task.status !== 'completed' && (
          <div className="mt-2 flex gap-1">
            {task.status !== 'in_progress' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('in_progress');
                }}
                className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Start
              </button>
            )}
            {task.status === 'in_progress' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('completed');
                }}
                className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Complete
              </button>
            )}
            {task.status !== 'blocked' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('blocked');
                }}
                className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Block
              </button>
            )}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
});